import DataSourcesDialog from "@/components/DataSourcesDialog";
import { Satellite, RefreshCw, Activity, MonitorPlay } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Header({ ledger, status, onSync, syncing, cascade, onCommandRoom }) {
  const online = status?.status === "complete" || (status?.asset_count || 0) > 0;
  return (
    <header
      data-testid="app-header"
      className="light-bar bg-white/95 backdrop-blur-xl border-b-2 border-cyan shadow-[0_6px_30px_rgba(1,33,115,0.25)] h-16 sm:h-24 flex items-center justify-between px-3 lg:px-6 gap-2 z-[1000] relative"
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <div className="shrink-0 [--lh:36px] sm:[--lh:72px]">
          {/* original logo, unmodified; only its transparent margin is clipped */}
          <div className="overflow-hidden" style={{ height: "var(--lh)", width: "calc(var(--lh) * 1860 / 613)" }}>
            <img data-testid="app-logo" src="/logo_full.png" alt="Sentinel Barbados — Every asset. Every storm. Every hour."
              className="block max-w-none"
              style={{ height: "calc(var(--lh) * 793 / 613)", width: "auto",
                       marginTop: "calc(var(--lh) * -74 / 613)", marginLeft: "calc(var(--lh) * -68 / 613)" }} />
          </div>
        </div>
        <h1 data-testid="app-title" className="sr-only">Sentinel Barbados</h1>
      </div>

      <div className="hidden md:flex items-center gap-2 lg:gap-3">
        <Kpi label="Assets" value={ledger?.total ?? "—"} testid="kpi-assets" />
        <Kpi
          label="Red Zones"
          value={ledger?.tiers?.critical ?? 0}
          accent="#FF3B30"
          testid="kpi-redzones"
        />
        <Kpi
          label="Readiness"
          value={ledger ? `${ledger.readiness}%` : "—"}
          accent="#30D158"
          testid="kpi-readiness"
        />
        <Kpi
          label="Isolated"
          value={cascade?.isolated_count ?? "—"}
          accent="#FF9F0A"
          testid="kpi-isolated"
        />
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="hidden lg:flex items-center gap-2 font-mono-t text-[10px] uppercase tracking-wider">
          <span
            className={`w-2 h-2 rounded-full ${online ? "bg-risk-low" : "bg-risk-critical"} ${
              syncing ? "animate-pulse" : ""
            }`}
          />
          <span className="text-[#012173] font-semibold flex items-center gap-1">
            <Satellite className="w-3.5 h-3.5" />
            {syncing ? "SYNCING" : online ? "SAT LINK ACTIVE" : "NO DATA"}
          </span>
        </div>
        <button data-testid="command-room-button" onClick={onCommandRoom}
          className="hidden md:flex items-center gap-1.5 rounded-md px-3 py-1.5 border border-[#012173]/30 bg-[#012173]/5 text-[#012173] hover:bg-[#012173]/10 font-mono-t text-[10px] uppercase tracking-wide transition-colors">
          <MonitorPlay className="w-3.5 h-3.5" /> Command room
        </button>
        <DataSourcesDialog />
        <Button
          data-testid="sync-data-button"
          onClick={onSync}
          disabled={syncing}
          className="bg-[#012173] text-cyan hover:bg-[#03268a] border border-cyan/60 font-heading font-semibold uppercase tracking-wide text-xs h-9 rounded-md"
        >
          {syncing ? (
            <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Activity className="w-4 h-4 sm:mr-1.5" />
          )}
          <span className="hidden sm:inline">{syncing ? "Ingesting" : "Sync Satellite Data"}</span>
        </Button>
      </div>
    </header>
  );
}

function Kpi({ label, value, accent = "#FCC109", testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg px-3 py-1.5 bg-[#012173] border border-[#012173] shadow-[inset_0_-2px_0_rgba(252,193,9,0.6)] flex flex-col items-start min-w-[78px]"
    >
      <span className="font-mono-t text-[9px] uppercase tracking-widest text-slate-300">{label}</span>
      <span className="font-heading font-bold text-lg leading-none" style={{ color: accent }}>
        {value}
      </span>
    </div>
  );
}
